import { TreeItem, TreeItemIndex } from "react-complex-tree";

type Tree = Record<TreeItemIndex, TreeItem>;
type ParentMap = Map<TreeItemIndex, TreeItemIndex>;

/**
 * Return a map giving the parent item (if it exists) of every item in the tree.
 */
export const generateTreeItemParentMap = (tree: Tree): ParentMap => {
  const parentMap = new Map();
  Object.values(tree).forEach((item) => {
    (item.children ?? []).forEach((childId) => {
      parentMap.set(childId, item.index);
    });
  });
  return parentMap;
};

/**
 * Return all children of the given item.
 */
const getTreeItemChildren = (item: TreeItemIndex, tree: Tree): TreeItemIndex[] => {
  return tree[item].children ?? [];
};

/**
 * Return all siblings of the given item.
 */
export const getTreeItemSiblings = (item: TreeItemIndex, tree: Tree, treeItemParentMap: ParentMap): TreeItemIndex[] => {
  const parentId = treeItemParentMap.get(item);
  if (!parentId) return [];

  return getTreeItemChildren(parentId, tree).filter((sibling) => sibling !== item);
};

/**
 * Return all ancestors of the given item.
 */
const getTreeItemAncestors = (items: Set<TreeItemIndex>, treeItemParentMap: ParentMap): Set<TreeItemIndex> => {
  const ancestry = new Set<TreeItemIndex>();

  let current = new Set<TreeItemIndex>(items);
  while (current.size > 0) {
    const next = new Set<TreeItemIndex>();
    current.forEach((item) => {
      const parent = treeItemParentMap.get(item);
      if (parent && !ancestry.has(parent)) {
        ancestry.add(parent);
        next.add(parent);
      }
    });
    current = next;
  }

  return ancestry;
};

type Return = {
  itemsToShow: TreeItemIndex[];
  itemsToHide: TreeItemIndex[];
};

/**
 * Resolve an entity visibility request (e.g. show layers A, B, and C) against the given tree.
 *
 * The resolution will ensure that if an entity is requested to become visible, then:
 *   - That entity will become visible,
 *   - All ancestors of that entity will become visible, and
 *   - Any invisible siblings of that entity (or any of its ancestors) not included in the request
 *     will remain invisible.
 */
export const resolveEntityVisibility = (
  entityVisibility: { [key: string]: boolean },
  tree: Record<TreeItemIndex, TreeItem>,
  treeItemParentMap: Map<TreeItemIndex, TreeItemIndex>,
  isTreeItemVisible: (item: TreeItemIndex) => boolean
): Return => {
  const itemsToShow = new Set<TreeItemIndex>();
  const itemsToHide = new Set<TreeItemIndex>();

  Object.entries(entityVisibility).forEach(([key, visible]) => {
    if (visible) {
      itemsToShow.add(key);
    } else {
      itemsToHide.add(key);
    }
  });

  // Showing an item always shows all its ancestors
  if (itemsToShow.size > 0) {
    const ancestors = getTreeItemAncestors(itemsToShow, treeItemParentMap);

    // Mark each ancestor as visible _before_ calculating implied sibling visibility
    ancestors.forEach((ancestor) => itemsToShow.add(ancestor));

    // Handle implied sibling visibility
    ancestors.forEach((ancestor) => {
      // If this ancestor was already visible then there's no implied anything.
      if (isTreeItemVisible(ancestor)) return;

      // If we're showing the ancestor but not the child => force the child invisible.
      getTreeItemChildren(ancestor, tree).forEach((child) => {
        if (!itemsToShow.has(child) && !isTreeItemVisible(child)) {
          itemsToHide.add(child);
        }
      });
    });
  }

  return { itemsToShow: Array.from(itemsToShow), itemsToHide: Array.from(itemsToHide) };
};
